import { AHIcon, AHText, Box, Circle, useLayoutContext, useTheme } from "arkh";
import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { OrgSpaceItem_storecastSpace$key } from "./__generated__/OrgSpaceItem_storecastSpace.graphql";
import { Pressable } from "react-native";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { StorecastSpace, useGetStorecastSpaceHeight } from "./StorecastSpace";
import { WidgetEvent } from "widgets/src/WidgetEventT";
import { WidgetSizeT } from "../../Storecast/Storecast/localTypes/WidgetSizeT";
import { useOrgSpaceUnitSize } from "./useOrgSpaceUnitSize";
import { SharedValue } from "react-native-reanimated";
import { FormattedMessage } from "react-intl";
import { OrgSpaceItemInstalled_StorecastSpace$key } from "./__generated__/OrgSpaceItemInstalled_StorecastSpace.graphql";
import { OrgButton } from "../OrgButton";
import { EDIT_LAYOUT_AND_WIDGETS } from "../../Storecast/Storecast/StorecastHeader/StorecastAdminPopover";
import { WorkspaceWritable } from "../workspaceId";

function OrgSpaceItemEditButton(props: {
  isEditing: boolean;
  onPress: () => void;
}) {
  const intl_tokens = useIntlTokens();
  return (
    <Box spaceBottom="small">
      <OrgButton
        title={props.isEditing ? intl_tokens.Done : EDIT_LAYOUT_AND_WIDGETS}
        backgroundColor={props.isEditing ? "primary" : "surface"}
        tintColor={props.isEditing ? "text-inverted" : "primary"}
        onPress={props.onPress}
        iconPostion="left"
        icon="edit"
      />
    </Box>
  );
}

const ORG_SPACE_ITEM_HEADER_HEIGHT = 50;

export function useGetOrgSpaceItemHeight() {
  const layout = useLayoutContext();
  const getStorecastSpaceHeight = useGetStorecastSpaceHeight();
  return useCallback(
    (height: number) => {
      return (
        getStorecastSpaceHeight(height) +
        ORG_SPACE_ITEM_HEADER_HEIGHT +
        layout.space.large * 2
      );
    },
    [layout.space.large, getStorecastSpaceHeight]
  );
}

export function OrgSpaceItem(props: {
  space: OrgSpaceItem_storecastSpace$key;
  openWidgetSettings?(_: {
    widgetId: string;
    __typename: string;
    size: WidgetSizeT;
  }): void;
  onAddWidget: (_: { spaceId: string; y: number; h: number }) => void;
  onEvent?:
    | ((_: { event: WidgetEvent; x: number; y: number }) => any)
    | undefined;
  hiddenWidgetId: SharedValue<string | null>;
  onShowStatus: (spaceId: string) => void;
  onEditSpace: (spaceId: string) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}) {
  const space = useFragment(
    graphql`
      fragment OrgSpaceItem_storecastSpace on StorecastSpace {
        id
        name
        ...StorecastSpace_storecastSpace
        ...OrgSpaceItemInstalled_StorecastSpace
      }
    `,
    props.space
  );
  const unitSize = useOrgSpaceUnitSize();
  const layout = useLayoutContext();
  const theme = useTheme();
  return (
    <Box
      cornerRadius="large"
      space="large"
      borderColor="border-primary"
      style={{
        borderWidth: 1,
        minWidth: 570,
        backgroundColor: theme.surface,
      }}
    >
      <Box
        style={{
          flexDirection: "row",
          height: ORG_SPACE_ITEM_HEADER_HEIGHT,
        }}
      >
        <WorkspaceWritable>
          <OrgSpaceItemEditButton
            isEditing={props.isEditing}
            onPress={() => props.setIsEditing(!props.isEditing)}
          />
        </WorkspaceWritable>
      </Box>
      <Box style={{ flexDirection: "row", flex: 1 }}>
        <StorecastSpace
          hiddenWidgetId={props.hiddenWidgetId}
          openWidgetSettings={props.openWidgetSettings}
          onAddWidget={({ y, h }) =>
            props.onAddWidget({ spaceId: space.id, y, h })
          }
          isEditing={props.isEditing}
          space={space}
          unitSize={unitSize}
          onEvent={props.onEvent}
        />
        <Box style={{ flex: 1 }} spaceLeft="large">
          <Box
            style={{ flexDirection: "row", alignItems: "center" }}
            spaceBottom="large"
          >
            <Box style={{ flex: 1 }}>
              <AHText type="title" spaceHorizontal="medium">
                {space.name}
              </AHText>
            </Box>
            <WorkspaceWritable>
              <Pressable
                onPress={() => {
                  props.onEditSpace(space.id);
                }}
                style={({ pressed }) => ({
                  paddingRight: layout.space.medium,
                  opacity: pressed ? 0.5 : 1,
                })}
              >
                <Circle size="medium" color="surface-backgroundControl">
                  <AHIcon type="more" size="medium" />
                </Circle>
              </Pressable>
            </WorkspaceWritable>
          </Box>
          <Installed
            space={space}
            onPress={() => props.onShowStatus(space.id)}
          />
        </Box>
      </Box>
    </Box>
  );
}

function Installed(props: {
  space: OrgSpaceItemInstalled_StorecastSpace$key;
  onPress: () => void;
}) {
  const space = useFragment(
    graphql`
      fragment OrgSpaceItemInstalled_StorecastSpace on StorecastSpace {
        id
        installedDashboardsCount
        installedStoresCount
        storesCount
      }
    `,
    props.space
  );

  return (
    <Item
      title={
        <FormattedMessage
          defaultMessage="Installed"
          description="Org Space Item Installed"
        />
      }
      subTitle={
        <FormattedMessage
          defaultMessage="{installedStoresCount}/{storesCount} Stores, {installedDashboardsCount} Dashboards"
          description="Org Space Item Installed Subtitle"
          values={{
            installedStoresCount: space.installedStoresCount,
            installedDashboardsCount: space.installedDashboardsCount,
            storesCount: space.storesCount,
          }}
        />
      }
      isFirst
      isLast
      onPress={props.onPress}
    />
  );
}

function Item(props: {
  title: React.ReactNode;
  subTitle: React.ReactNode;
  isLast?: boolean;
  isFirst?: boolean;
  onPress: () => void;
}) {
  const theme = useTheme();
  const layout = useLayoutContext();
  return (
    <Pressable
      onPress={props.onPress}
      style={({ pressed }) => ({
        opacity: pressed ? 0.5 : 1,
        borderWidth: 1,
        borderColor: theme["border-primary"],
        borderTopLeftRadius: props.isFirst ? layout.radius.large : undefined,
        borderTopRightRadius: props.isFirst ? layout.radius.large : undefined,
        borderBottomLeftRadius: props.isLast ? layout.radius.large : undefined,
        borderBottomRightRadius: props.isLast ? layout.radius.large : undefined,
        flexDirection: "row",
        alignItems: "center",
        borderTopWidth: props.isFirst ? 1 : 0,
        borderBottomWidth: props.isLast ? 1 : 0,
        padding: layout.space.medium,
      })}
    >
      <Box style={{ flex: 1 }}>
        <AHText type="title-small">{props.title}</AHText>
        {props.subTitle ? (
          <AHText type="body" spaceTop="xsmall">
            {props.subTitle}
          </AHText>
        ) : null}
      </Box>
      <AHIcon type="chevron-right" color="washedText" />
    </Pressable>
  );
}
